import { UserAppointmentPermissionItem, UserLocationPermissionItem, UserResourcePermissionItem, UserShopPermissionItem } from '../user-permission-item/user-permission-item'
import { UserPermissionGroup } from '../user-permission-group/user-permission-group'
import { useTranslate, useWindowDimensions } from 'utils'
import { Drawer, theme } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { FormSection } from 'components/form-section/form-section'
import { Loading, NotFound, PageError, VerticalContainer } from 'components/styled'
import { UserPermissionRouter } from 'components/user/user-permission-router/user-permission-router'
import { useEffect, useState } from 'react'
import { PermissionLevel, PermissionType, PermissionTree } from 'types'
import { useField } from 'react-final-form'
import { usePermissionTree } from 'api'

type GroupedPermissions = {
    [PermissionType.Shop       ]: string[]
    [PermissionType.Location   ]: string[]
    // [PermissionType.Service    ]: string[]
    [PermissionType.Resource   ]: string[]
    [PermissionType.Appointment]: string[]
    // [PermissionType.Flow       ]: string[]
}

export type InheritedPermissions = [PermissionType, PermissionLevel][]

export const UserPermissionList = () => {

    //hooks:
    const { __ } = useTranslate()
    const { token: { margin, paddingLG, colorTextDescription, colorErrorText, fontSize } } = theme.useToken()
    const { width, SM } = useWindowDimensions()

    //api:
    const { data: permissionTree, isLoading } = usePermissionTree()
    const { input: { value: permissions, onChange: updatePermissions }, meta: { touched, error } } = useField('permissions')

    //state:
    const [ isDrawerOpen, setIsDrawerOpen ] = useState(false)
    const [ groupedPermissions, setGroupedPermissions] = useState(groupPermissions(permissions))
    const [ inheritedPermissions, setInheritedPermissions ] = useState([] as InheritedPermissions)

    //group permissions:
    useEffect(() => setGroupedPermissions(groupPermissions(permissions)), [permissions])

    //inherited permissions:
    useEffect(() => {
        let inheritedPermissions: [PermissionType, PermissionLevel][] = []
        if(permissions.length > 0 && permissionTree){
            inheritedPermissions = findInheritedPermissions(permissions, permissionTree)
        }
        setInheritedPermissions(inheritedPermissions)
    }, [ permissions, permissionTree ])

    return (
        <FormSection
            title={__`permissions`}
            topButton={{
                label: __`grant_permission`,
                icon: <PlusOutlined/>,
                onClick: () => setIsDrawerOpen(true)
            }}
            style={{ paddingBottom: SM ? paddingLG : 0 }}
        >

            {hasNoPermission(groupedPermissions) ?
                
                <NotFound
                    message={__`no_permissions`}
                    style={{
                        color: (touched && error) ? colorErrorText : colorTextDescription,
                        paddingBottom: paddingLG,
                        fontSize: fontSize
                    }}
                /> : (

                isLoading ? <Loading/> : !permissionTree ? <PageError/> : (

                    <VerticalContainer style={{ gap: margin }}>

                        {/* shop */}
                        {groupedPermissions.Shop.length > 0 && (
                            <UserPermissionGroup title={__`shop`}>
                                {groupedPermissions.Shop.map(permission =>
                                    <UserShopPermissionItem
                                        key={permission}
                                        permission={permission}
                                        onRemove={() => updatePermissions(handleRemovePermission(permissions, permission))}
                                    />
                                )}
                            </UserPermissionGroup>
                        )}

                        {/* location */}
                        {groupedPermissions.Location.length > 0 && (
                            <UserPermissionGroup title={__`location`}>
                                {groupedPermissions.Location.map(permission =>
                                    <UserLocationPermissionItem
                                        key={permission}
                                        permission={permission}
                                        onRemove={() => updatePermissions(handleRemovePermission(permissions, permission))}
                                    />
                                )}
                            </UserPermissionGroup>
                        )}

                        {/* service */}
                        {/* {groupedPermissions.Service.length > 0 && (
                            <UserPermissionGroup title={__`service`}>
                                {groupedPermissions.Service.map(permission =>
                                    <UserServicePermissionItem
                                        key={permission}
                                        permission={permission}
                                        onRemove={() => updatePermissions(handleRemovePermission(permissions, permission))}
                                    />
                                )}
                            </UserPermissionGroup>
                        )} */}

                        {/* resource */}
                        {groupedPermissions.Resource.length > 0 && (
                            <UserPermissionGroup title={__`resource`}>
                                {groupedPermissions.Resource.map(permission =>
                                    <UserResourcePermissionItem
                                        key={permission}
                                        permission={permission}
                                        onRemove={() => updatePermissions(handleRemovePermission(permissions, permission))}
                                    />
                                )}
                            </UserPermissionGroup>
                        )}

                        {/* appointment */}
                        {groupedPermissions.Appointment.length > 0 &&
                            <UserPermissionGroup title={__`appointment`}>
                                {groupedPermissions.Appointment.map(permission =>
                                    <UserAppointmentPermissionItem
                                        key={permission}
                                        permission={permission}
                                        onRemove={() => updatePermissions(handleRemovePermission(permissions, permission))}
                                    />
                                )}
                            </UserPermissionGroup>
                        }

                        {/* flow */}
                        {/* {groupedPermissions.Flow.length > 0 && (
                            <UserPermissionGroup title={__`flow`}>
                                {groupedPermissions.Flow.map(permission =>
                                    <UserFlowPermissionItem
                                        key={permission}
                                        permission={permission}
                                        onRemove={() => updatePermissions(handleRemovePermission(permissions, permission))}
                                    />
                                )}
                            </UserPermissionGroup>
                        )} */}

                    </VerticalContainer>

                ))
                
            }

            {/* drawer */}
            <Drawer
                closable={false}
                width={SM ? 450 : width } // full width in mobile
                styles={{ body: { padding: 0 }}}
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
            >
                
                <UserPermissionRouter
                    inheritedPermissions={inheritedPermissions}
                    onAdd={permission => updatePermissions(getNewPermissions(permissions, permission))}
                    onBack={() => setIsDrawerOpen(false)}
                />
                
            </Drawer>

        </FormSection>
    )
    
}

//initPermissionState:
const groupPermissions = (permissions: string[]) => {

    //empty state:
    const groupedPermissions = {
        [PermissionType.Shop       ]: [],
        [PermissionType.Location   ]: [],
        // [PermissionType.Service    ]: [],
        [PermissionType.Resource   ]: [],
        [PermissionType.Appointment]: [],
        // [PermissionType.Flow       ]: []
    } as GroupedPermissions
    
    //group the permissions:
    permissions.map(permission => {
        const splitPermission = permission.split(':')
        const type = splitPermission[0] as keyof GroupedPermissions
        groupedPermissions[type] = removeAllPermissionsLevels(groupedPermissions[type], permission)
        groupedPermissions[type].push(permission)
        groupedPermissions[type] = [...moveAllToTop(groupedPermissions[type])]
    })

    return groupedPermissions
    
}

const moveAllToTop = (permissions: string[])  => {
    return [
        ...permissions.filter(per => per.split(':')[1] === 'All'),
        ...permissions.filter(per => per.split(':')[1] !== 'All')
    ]
}

//findInheritedPermissions:
const findInheritedPermissions = (permissions: string[], permissionTree: PermissionTree) => {
    const inheritedPermissions: [PermissionType, PermissionLevel][] = []
    permissions.map(permission => {
        const splitPermission = permission.split(':')
        const permissionType = splitPermission[0] as keyof PermissionTree
        const permissionLevel = splitPermission[2] as PermissionLevel
        if(permissionTree[permissionType]){
            permissionTree[permissionType][permissionLevel].map(per => {
                inheritedPermissions.push(per)
            })
        }
    })
    return inheritedPermissions
}

//hasNoPermission:
const hasNoPermission = (groupedPermissions: GroupedPermissions) => !(
    groupedPermissions.Shop.length        ||
    groupedPermissions.Location.length    ||
    // groupedPermissions.Service.length  ||
    groupedPermissions.Resource.length    ||
    groupedPermissions.Appointment.length
    // groupedPermissions.Flow.length
)

//getNewPermissions:
const getNewPermissions = (permissions: string[], newPermission: string) => {
    if(permissions.indexOf(newPermission) === -1){
        const newPermissions = removeAllPermissionsLevels(permissions, newPermission)
        newPermissions.push(newPermission)
        return newPermissions
    }
    return permissions
}

//removeAllPermissionsLevels:
const removeAllPermissionsLevels = (permissions: string[], permission: string) => {
    
    //get fields from permission:
    const splitPermission = permission.split(':')
    const type = splitPermission[0] as keyof GroupedPermissions
    const id = splitPermission[1]
    
    //remove same read:
    const newPermissions = [...permissions]
    const readIndex = newPermissions.indexOf(`${type}:${id}:${PermissionLevel.Read  }`)
    readIndex >= 0 && newPermissions.splice(readIndex, 1)
    
    //remove same write:
    const writeIndex = newPermissions.indexOf(`${type}:${id}:${PermissionLevel.Write  }`)
    writeIndex >= 0 && newPermissions.splice(writeIndex, 1)
    
    return newPermissions

}

//handleRemovePermission:
const handleRemovePermission = (permissions: string[], permission: string) => {
    const newPermissions = [...permissions]
    newPermissions.splice(newPermissions.indexOf(permission), 1)
    return newPermissions
}
